/**
 * SelectTextQuiz Adult Renderer
 * Thin rendering component that delegates logic to useSelectTextQuiz hook.
 */

import { useMemo, memo, ReactNode, useRef, useState, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../../lib/utils'
import { KatexText, QuizResultFeedback, SubmitButton, QUIZ_INLINE_STYLES, QuizSolveDescription, QuizHeader } from '../shared'
import { formatProblem } from '../../../utils/quiz-parser'
import type { SelectTextQuizData, QuizSolveResult } from '../../../types/legacyQuiz'
import { parseSelectTextOptionModels, useSelectTextQuiz, type SelectTextOptionModel } from '../hooks'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Check } from 'lucide-react'

// Portal 드롭다운 컴포넌트 - body에 렌더링하여 clipping 방지
interface PortalDropdownProps {
  isOpen: boolean
  anchorRef: React.RefObject<HTMLButtonElement | null>
  options: SelectTextOptionModel[]
  selectedOptionIdx: number | null
  onSelect: (optIdx: number) => void
  onClose: () => void
}

function PortalDropdown({ isOpen, anchorRef, options, selectedOptionIdx, onSelect, onClose }: PortalDropdownProps) {
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const dropdownRef = useRef<HTMLDivElement>(null)

  // 위치 계산
  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) return

    const updatePosition = () => {
      const rect = anchorRef.current?.getBoundingClientRect()
      if (!rect) return

      const dropdownHeight = dropdownRef.current?.offsetHeight || 200
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top

      // 아래 공간이 부족하면 위로 펼침
      const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

      setPosition({
        top: showAbove ? rect.top + window.scrollY - dropdownHeight - 4 : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      })
    }

    updatePosition()

    // 스크롤/리사이즈 시 닫기
    const handleScrollOrResize = () => onClose()
    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [isOpen, anchorRef, onClose])

  if (!isOpen) return null

  return createPortal(
    <AnimatePresence>
      <motion.div
        ref={dropdownRef}
        initial={{ opacity: 0, y: -10, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -10, scale: 0.95 }}
        style={{ position: 'absolute', top: position.top, left: position.left }}
        className="z-9999 bg-white rounded-xl border border-gray-200 shadow-xl min-w-32"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {options.map((option) => {
          if (!option.isSelectable) return null
          return (
            <button
              key={option.rawIndex}
              onClick={() => onSelect(option.rawIndex)}
              className={cn(
                'w-full px-4 py-3 text-left text-lg font-medium transition-colors flex items-center gap-2',
                'hover:bg-blue-50',
                selectedOptionIdx === option.rawIndex && 'bg-blue-100 text-blue-700'
              )}
            >
              {selectedOptionIdx === option.rawIndex && (
                <Check className="w-4 h-4 text-blue-600" />
              )}
              <KatexText text={option.label} />
            </button>
          )
        })}
      </motion.div>
    </AnimatePresence>,
    document.body
  )
}

interface SelectTextQuizProps {
  quizId: string | number
  quiz: SelectTextQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[] // 제출된 답안 (부모에서 전달, 채점 후에도 유지)
  submitLabel?: string // 제출 버튼 라벨 (default: '정답 확인')
  hideSubmitButton?: boolean // 제출 버튼 숨김 (체크업 등에서 외부 버튼 사용 시)
  onAnswerChange?: (answers: string[]) => void // 답변 변경 시 콜백
  disableSound?: boolean // 사운드 비활성화 (리뷰 모드 등)
  difficultyLevel?: number | null // 난이도 (1-5)
  /** G3 — true면 풀이 후 해설(QuizSolveDescription)을 숨긴다(정오답 피드백은 유지). 기본 false=현행 노출. */
  hideExplanation?: boolean
}

// 개별 선택 영역 컴포넌트 - memo로 감싸서 불필요한 리렌더 방지
interface SelectBlankProps {
  quizId: string | number
  index: number
  content: string
  selectedOptionIdx: number | null
  isActive: boolean
  isOpen: boolean
  isSolved: boolean
  isViewSolved: boolean
  isCorrectAnswer: boolean
  shouldShowCorrectAnswers: boolean
  correctAnswerIndex: number | null  // 서버에서 받은 정답 인덱스
  onToggle: (index: number) => void
  onSelect: (selectIndex: number, optionIndex: number) => void
}

const SelectBlank = memo(function SelectBlank({
  quizId,
  index,
  content,
  selectedOptionIdx,
  isActive,
  isOpen,
  isSolved,
  isViewSolved,
  isCorrectAnswer,
  shouldShowCorrectAnswers,
  correctAnswerIndex,
  onToggle,
  onSelect,
}: SelectBlankProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const { t } = useTranslation('quiz')
  // 원문 인덱스를 보존하면서 표시용 label과 sentinel 여부를 파싱한다.
  const options = useMemo(() => {
    return parseSelectTextOptionModels(content)
  }, [content])

  const selectedOption = selectedOptionIdx !== null && options[selectedOptionIdx]?.isSelectable
    ? options[selectedOptionIdx]
    : null
  const showAsCorrect = isSolved && isCorrectAnswer
  const showAsWrong = isSolved && selectedOptionIdx !== null && !isCorrectAnswer && !shouldShowCorrectAnswers
  const correctAnswer = options[correctAnswerIndex ?? 0]?.label ?? options.find(option => option.isSelectable)?.label ?? ''

  const handleSelect = useCallback((optIdx: number) => {
    onSelect(index, optIdx)
  }, [index, onSelect])

  const handleClose = useCallback(() => {
    onToggle(index)
  }, [index, onToggle])

  return (
    <span key={`select-${quizId}-${index}`} className="inline-block my-1">
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation()
          onToggle(index)
        }}
        disabled={isSolved || isViewSolved}
        className={cn(
          QUIZ_INLINE_STYLES.base,
          QUIZ_INLINE_STYLES.select,
          'gap-1 border',
          !isSolved && !isActive && !selectedOption && 'border-gray-300 text-gray-400',
          isActive && !selectedOption && 'border-blue-500 ring-2 ring-blue-100',
          !isSolved && selectedOption && 'border-blue-500 bg-blue-50 text-blue-700',
          showAsCorrect && QUIZ_INLINE_STYLES.correct,
          showAsWrong && QUIZ_INLINE_STYLES.wrong,
          shouldShowCorrectAnswers && isSolved && !isCorrectAnswer && 'border-green-500 bg-green-50 text-green-700',
          (isSolved || isViewSolved) && 'cursor-not-allowed'
        )}
      >
        <span>
          {shouldShowCorrectAnswers && isSolved && !isCorrectAnswer
            ? <KatexText text={correctAnswer} />
            : selectedOption
              ? <KatexText text={selectedOption.label} />
              : t('types.selectText.placeholder')}
        </span>
        {!isSolved && !isViewSolved && (
          <ChevronDown className={cn(
            'w-4 h-4 transition-transform',
            isOpen && 'rotate-180'
          )} />
        )}
      </button>

      {/* Portal 기반 드롭다운 - body에 렌더링하여 clipping 방지 */}
      <PortalDropdown
        isOpen={isOpen && !isSolved && !isViewSolved}
        anchorRef={buttonRef}
        options={options}
        selectedOptionIdx={selectedOptionIdx}
        onSelect={handleSelect}
        onClose={handleClose}
      />
    </span>
  )
})

export function SelectTextQuizAdult({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound = false,
  difficultyLevel,
  hideExplanation = false,
}: SelectTextQuizProps) {
  const { t } = useTranslation('quiz')

  const {
    answers,
    currentIndex,
    openDropdown,
    selectCount,
    problemKey,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggleDropdown,
    handleSelect,
    handleSubmit,
  } = useSelectTextQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange,
  })

  // 문제 렌더링 - useMemo로 안정화 (드롭다운 토글/정답확인 시 리마운트 방지)
  const renderedProblem = useMemo<ReactNode[]>(() => {
    if (answers.length === 0 && selectCount > 0) return []

    return formatProblem(
      quiz.problem,
      `${quizId}-problem-${problemKey}`,
      (index: number, content: string) => {
        const selectedOptionIdx = answers[index] !== '' ? parseInt(answers[index]) : null
        const isActive = currentIndex === index && !isSolved && !isViewSolved
        const isOpen = openDropdown === index

        // 정답 여부 확인 (solveResult.corrects 사용)
        const isCorrectByCorrects = solveResult?.corrects?.[index]
        const isCorrectAnswer = (isCorrectByCorrects !== undefined && isCorrectByCorrects !== null) ? isCorrectByCorrects : selectedOptionIdx === 0

        // 서버에서 받은 정답 인덱스 (정답보기용)
        const correctAnswerFromServer = solveResult?.correctAnswers?.[index]
        const correctAnswerIndex = typeof correctAnswerFromServer === 'number'
          ? correctAnswerFromServer
          : (typeof correctAnswerFromServer === 'string' ? parseInt(correctAnswerFromServer) : null)

        return (
          <SelectBlank
            key={`select-${quizId}-${problemKey}-${index}`}
            quizId={quizId}
            index={index}
            content={content}
            selectedOptionIdx={selectedOptionIdx}
            isActive={isActive}
            isOpen={isOpen}
            isSolved={isSolved}
            isViewSolved={isViewSolved}
            isCorrectAnswer={isCorrectAnswer}
            shouldShowCorrectAnswers={shouldShowCorrectAnswers}
            correctAnswerIndex={correctAnswerIndex}
            onToggle={handleToggleDropdown}
            onSelect={handleSelect}
          />
        )
      },
      quiz.additionalImages ?? undefined
    )
  }, [answers, currentIndex, openDropdown, isSolved, shouldShowCorrectAnswers, quiz.problem, quizId, isViewSolved, selectCount, quiz.additionalImages, solveResult?.corrects, solveResult?.correctAnswers, problemKey, handleToggleDropdown, handleSelect])

  return (
    <div className="space-y-6">
      {/* 문제 헤더 */}
      <QuizHeader
        quizId={quizId}
        typeLabel={t('types.selectText.label')}
        title={quiz.title}
        difficultyLevel={difficultyLevel}
        image={quiz.image}
        imageMobile={quiz.imageMobile}
        desmos={quiz.desmos}
        vars={quiz.vars}
      />

      {/* 문제 본문 - 기획 데모 .passage-area 스타일 */}
      <div
        className={cn(
          'p-4 bg-gray-100 rounded-xl',
          'text-xl leading-relaxed text-gray-700'
        )}
      >
        {renderedProblem}
      </div>

      {/* 정답 확인 버튼 */}
      {!isSolved && !isViewSolved && !hideSubmitButton && (
        <SubmitButton
          onClick={handleSubmit}
          disabled={!canSubmit}
          label={submitLabel}
        />
      )}

      {/* 결과 피드백 */}
      {isSolved && (
        <QuizResultFeedback
          isCorrect={solveResult?.correct ?? false}
          show={isSolved}
          disableSound={disableSound}
          isViewSolved={isViewSolved}
        />
      )}

      {/* 해설 */}
      {isSolved && solveResult?.description && !hideExplanation && (
        <QuizSolveDescription
          description={solveResult.description}
          commentaryImages={quiz.commentaryImages}
          vars={quiz.vars}
          id={`${quizId}-desc`}
          actionVisible={!solveResult.correct}
          actionLabel={isShowingCorrectAnswers ? t('common.viewMyAnswer') : t('common.viewCorrectAnswer')}
          onActionClick={() => setIsShowingCorrectAnswers(v => !v)}
        />
      )}
    </div>
  )
}
